"use client";
import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { toast } from "react-toastify";
import { CustomErrorBoundary } from "../../shared";

import { CmailIcon, PhoneIcon } from "@icons/svgs";
import styles from "@styles/contact.module.css";

type FormData = {
  first_name: string;
  last_name: string;
  number: string;
  email: string;
  message: string;
};

export function Contact() {
  return <ContactElement />;
}
export function ContactElement() {
  const [message, setMessage] = useState<string>("");
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const form = useForm<FormData>();
  const { register, handleSubmit, formState, reset, setValue } = form;
  const { errors, isDirty, isSubmitting } = formState;

  const handleChange =
    (name: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const trimedValue = e.target.value.trim();
      setValue(name, trimedValue, { shouldValidate: true });
    };

  const submitForm: SubmitHandler<FormData> = async (data) => {
    setButtonDisabled(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const resData = await response.json();
      const statusCode = response.status;
      if (statusCode === 200) {
        reset();
        setMessage("Message sent successfully! We will respond shortly.");
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
        }, 3000);
      } else if (statusCode === 429) {
        reset();
        setMessage(
          "Too many requests! You can submit only two request per day.",
        );
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
        }, 3000);
      } else if (statusCode === 400) {
        setMessage("Bad Request");
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
        }, 3000);
      } else {
        setMessage("Internal Server Error");
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
        }, 3000);
      }
      return resData;
    } catch (error: any) {
      console.error(error);
      toast.error(`${error.data?.error_message || error.error}`);
      reset();
      setMessage("Too many requests! You can submit only two request per day.");
      setTimeout(() => {
        setMessage("");
        setButtonDisabled(false);
      }, 3000);
    }
  };

  return (
    <div className={styles.contact_container}>
      <div className={styles.contact_inner} data-aos="zoom-in">
        <span className="text-oicnm-green">Contact us</span>
        <h2>Get in Touch with Us Today!</h2>
        <div className={styles.contact_flex}>
          <div className={styles.contact_item}>
            <div className={styles.contact_icon_case} aria-hidden="true">
              <PhoneIcon />
            </div>
            <div className={styles.contact_text_case}>
              <h3>PHONE</h3>
              <span>(+234) 9069 730 290</span>
            </div>
          </div>
          <div className={styles.contact_item}>
            <div className={styles.contact_icon_case} aria-hidden="true">
              <CmailIcon />
            </div>
            <div className={styles.contact_text_case}>
              <h3>MAIL</h3>
              <span style={{ textTransform: "lowercase" }}>
                info@theoicnm.com
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(submitForm)} noValidate>
          <div className={styles.form_flex}>
            <div className={styles.input_grp}>
              <input
                {...register("first_name", {
                  required: "Please enter first name!",
                  pattern: {
                    value: /^[a-zA-Z-']+$/,
                    message: "Use only letters, hyphen or single quote",
                  },
                  validate: {
                    tooLong: (fieldValue) => {
                      return fieldValue.length < 31 || "Max of 30 letters!";
                    },
                    tooShort: (fieldValue) => {
                      return fieldValue.length > 2 || "Min of 3 letters";
                    },
                  },
                })}
                type="text"
                autoComplete="given-name"
                aria-label="First name"
                aria-invalid={!!errors.first_name}
                aria-describedby={
                  errors.first_name ? "home_first_name-error" : undefined
                }
                placeholder="First Name"
                id="first_name"
                onChange={handleChange("first_name")}
              />
              {errors.first_name && (
                <p id="home_first_name-error" role="alert">
                  {errors.first_name.message}
                </p>
              )}
            </div>
            <div className={styles.input_grp}>
              <input
                {...register("last_name", {
                  required: "Please enter last name!",
                  pattern: {
                    value: /^[a-zA-Z-']+$/,
                    message: "Use letters, hyphen or single quote",
                  },
                  validate: {
                    tooLong: (fieldValue) => {
                      return fieldValue.length < 31 || "Max of 30 letters!";
                    },
                    tooShort: (fieldValue) => {
                      return fieldValue.length > 2 || "Min of 3 letters";
                    },
                  },
                })}
                type="text"
                autoComplete="family-name"
                aria-label="Last name"
                aria-invalid={!!errors.last_name}
                aria-describedby={
                  errors.last_name ? "home_last_name-error" : undefined
                }
                placeholder="Last Name"
                id="last_name"
                onChange={handleChange("last_name")}
              />
              {errors.last_name && (
                <p id="home_last_name-error" role="alert">
                  {errors.last_name.message}
                </p>
              )}
            </div>
          </div>
          <div className={styles.form_flex}>
            <div className={styles.input_grp}>
              <input
                {...register("number", {
                  required: "Please enter phone number!",
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a valid phone number",
                  },
                  validate: {
                    tooShort: (fieldValue) => {
                      return (
                        fieldValue.toString().length > 9 ||
                        "Please enter a valid phone number"
                      );
                    },
                  },
                })}
                type="tel"
                autoComplete="tel"
                aria-label="Phone number"
                aria-invalid={!!errors.number}
                aria-describedby={
                  errors.number ? "home_number-error" : undefined
                }
                placeholder="Phone Number"
                id="number"
                onChange={handleChange("number")}
              />
              {errors.number && (
                <p id="home_number-error" role="alert">
                  {errors.number.message}
                </p>
              )}
            </div>
            <div className={styles.input_grp}>
              <input
                {...register("email", {
                  required: "Please enter email!",
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Please enter a valid email!",
                  },
                  validate: {
                    notThisEmail: (fieldValue) => {
                      return (
                        fieldValue != "admin@example.com" ||
                        "Please enter a different email"
                      );
                    },
                    notBadEmail: (fieldValue) => {
                      return (
                        !fieldValue.endsWith("@example.com") ||
                        "Please enter a different email"
                      );
                    },
                  },
                })}
                type="email"
                autoComplete="email"
                aria-label="Email address"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "home_email-error" : undefined}
                placeholder="Email"
                id="email"
              />
              {errors.email && (
                <p id="home_email-error" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>
          <div className={styles.textarea_grp}>
            <textarea
              {...register("message", { required: "Message cannot be empty!" })}
              id="message"
              rows={5}
              aria-label="Your message"
              aria-invalid={!!errors.message}
              aria-describedby={
                errors.message ? "home_message-error" : undefined
              }
              placeholder="Enter your message here..."
            ></textarea>
            {errors.message && (
              <p id="home_message-error" role="alert">
                {errors.message.message}
              </p>
            )}
          </div>
          <p className={styles.submit_success} role="status">
            {message}
          </p>
          <button
            disabled={!isDirty || isSubmitting || buttonDisabled}
            className="p-4 bg-oicnm-green text-white"
          >
            {isSubmitting ? "Submiting" : "Submit Now"}
          </button>
        </form>
      </div>
    </div>
  );
}
